import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { progressLabel, type MomentumMonth } from "@/lib/momentum";

/**
 * Folder tones — one family, where colour shows activity (Figma 09 · Momentum): paper for a
 * quiet month, lilac for a month that holds steps, a stronger lilac for the current month.
 * `back` is the back panel and tab, `front` the flap (a soft lilac-to-blush wash on the
 * lilac tones), `panel` the open month's surface, `ink` the writing on the flap.
 */
export const FOLDER_TONES = {
  quiet: {
    back: "#e5dfd6",
    front: "#f0ece5",
    panel: "rgb(240 236 229)",
    ink: "rgb(118 105 95 / 0.75)",
  },
  active: {
    back: "#d4cbea",
    front: "linear-gradient(90deg, #ece6f7 0%, #f6eaef 100%)",
    panel: "rgb(236 230 247)",
    ink: "#48396a",
  },
  current: {
    back: "#c5bbdd",
    front: "linear-gradient(90deg, #e1daf1 0%, #f0e1ee 100%)",
    panel: "rgb(225 218 241)",
    ink: "#48396a",
  },
} as const;

export type FolderTone = (typeof FOLDER_TONES)[keyof typeof FOLDER_TONES];

export function folderTone(month: Pick<MomentumMonth, "isCurrent" | "items">): FolderTone {
  if (month.isCurrent) return FOLDER_TONES.current;
  return month.items.length > 0 ? FOLDER_TONES.active : FOLDER_TONES.quiet;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function monthName(month: number) {
  return MONTH_NAMES[month];
}

/** Paper slips peeking over the back of a folder that holds steps. */
const SLIPS = [
  { left: "24%", width: "40%", rotate: -4, rest: 2 },
  { left: "42%", width: "38%", rotate: 3, rest: 6 },
  { left: "60%", width: "30%", rotate: 7, rest: 11 },
];

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

interface Props {
  month: MomentumMonth;
  open: boolean;
  onToggle: () => void;
  panelId: string;
}

export function MonthFolder({ month, open, onToggle, panelId }: Props) {
  const reduce = useReducedMotion();
  const tone = folderTone(month);
  const total = month.items.length;
  const hasItems = total > 0;
  const allDone = hasItems && month.done === total;
  const slips = SLIPS.slice(0, Math.min(total, SLIPS.length));
  const name = monthName(month.month);

  const status = hasItems
    ? `${total} ${total === 1 ? "step" : "steps"}, ${month.done} done`
    : "no steps yet";

  return (
    <motion.button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
      aria-label={`${name} ${month.year}${month.isCurrent ? " (this month)" : ""} — ${status}`}
      data-momentum-folder={month.key}
      className="group relative flex w-full scroll-mt-24 flex-col items-center rounded-3xl pt-2 pb-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-4 focus-visible:ring-offset-background"
      initial={false}
      animate={open ? "open" : "closed"}
      whileHover={reduce || open ? undefined : "hover"}
      whileTap={reduce ? undefined : { scale: 0.985 }}
    >
      {/* The folder itself */}
      <motion.div
        className="relative w-full"
        style={{ aspectRatio: "5 / 3.6", perspective: 700 }}
        variants={{
          closed: { y: 0 },
          hover: { y: -3 },
          open: { y: -2 },
        }}
        transition={{ duration: 0.16, ease: "easeOut" }}
      >
        {/* Ground shadow — deepens as the folder lifts */}
        <motion.div
          aria-hidden
          className="absolute inset-x-[8%] -bottom-1 h-4 rounded-[50%] bg-foreground/15 blur-md"
          variants={{
            closed: { opacity: 0.55, scaleX: 0.92 },
            hover: { opacity: 0.8, scaleX: 1 },
            open: { opacity: 0.85, scaleX: 1 },
          }}
          transition={{ duration: 0.16 }}
        />

        {/* Back panel + tab */}
        <div
          aria-hidden
          className="absolute left-0 top-[4%] h-[20%] w-[40%] rounded-t-[14px]"
          style={{ background: tone.back }}
        />
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 top-[12%] rounded-[18px]"
          style={{ background: tone.back }}
        />

        {/* Paper slips — the month's steps, peeking out */}
        {slips.map((s, i) => (
          <motion.div
            key={i}
            aria-hidden
            className="absolute top-[6%] h-[56%] rounded-[6px] border border-foreground/[0.06] bg-[#fdfdfb] shadow-[0_1px_1px_rgba(40,37,34,0.08)]"
            style={{ left: s.left, width: s.width, rotate: s.rotate, originY: 1 }}
            variants={{
              closed: { y: s.rest, opacity: 1 },
              hover: { y: s.rest - 5, opacity: 1 },
              open: { y: s.rest - 16, opacity: 0 },
            }}
            transition={{ duration: open ? 0.26 : 0.18, ease: EASE_OUT, delay: open ? i * 0.03 : 0 }}
          >
            <div className="mx-[12%] mt-[14%] space-y-[9%]">
              <div className="h-[3px] w-4/5 rounded-full bg-stone-300/70" />
              <div className="h-[3px] w-3/5 rounded-full bg-stone-300/50" />
            </div>
          </motion.div>
        ))}

        {/* Front flap — tilts forward when the folder opens */}
        <motion.div
          aria-hidden
          className="absolute inset-x-0 bottom-0 top-[26%] rounded-[18px] shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]"
          style={{ background: tone.front, originY: 1, transformPerspective: 700 }}
          variants={{
            closed: { rotateX: 0, y: 0 },
            hover: { rotateX: reduce ? 0 : -6, y: 0 },
            open: { rotateX: reduce ? 0 : -24, y: reduce ? 0 : 2 },
          }}
          transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 32, mass: 0.8 }}
        >
          {/* Progress, written on the flap */}
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-[9%]">
            {hasItems ? (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold tracking-wide sm:text-xs"
                style={{ color: tone.ink }}
              >
                {allDone && <Check className="h-3 w-3" strokeWidth={3} />}
                {progressLabel(month.done, total)}
              </span>
            ) : (
              <span className="text-[11px] font-medium tracking-wide sm:text-xs" style={{ color: tone.ink, opacity: 0.55 }}>
                Quiet month
              </span>
            )}
            {hasItems && !allDone && (
              <span className="hidden h-1 w-10 overflow-hidden rounded-full bg-white/55 sm:block">
                <span
                  className="block h-full rounded-full bg-primary opacity-55"
                  style={{ width: `${(month.done / total) * 100}%` }}
                />
              </span>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Label */}
      <span className="mt-3 flex items-center gap-2">
        <span
          className={cn(
            "font-display text-lg font-semibold leading-5 tracking-[-0.01em] text-foreground sm:text-xl",
            !hasItems && "text-foreground/60",
          )}
        >
          {name}
        </span>
        {month.isCurrent && (
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-primary">
            <span className="h-1 w-1 rounded-full bg-primary" />
            Now
          </span>
        )}
      </span>
    </motion.button>
  );
}
